import { Space } from 'antd'
import {
  ProjectFdcConcurrenceGroupsQuery,
  useCreateFdcConcurrenceGroupMutation,
  useEditFdcConcurrenceGroupMutation,
  useProjectFdcConcurrenceGroupsQuery,
  useRestoreFdcConcurrenceGroupMutation,
  useSoftRemoveFdcConcurrenceGroupMutation,
} from 'schema'
import {
  DefaultMutationHookOptions,
  DefaultQueryHookOptions,
} from 'utils/defaults'
import { useGlobalState } from 'context/GlobalStateContext'
import CIMSTable from 'components/CIMSTable'
import { CIMSColumn } from 'components/CIMSTable/CIMSColumn/types'
import { useNameColumn } from 'components/CIMSTable/common/hooks/useNameColumn'
import { useUsersAndRolesColumn } from 'components/CIMSTable/common/hooks/useUsersAndRolesColumn'
import { useOptionalDescriptionColumn } from 'components/CIMSTable/common/hooks/useDescriptionColumn'
import { HelpMessage } from 'utils/constants'
import CreateEditModalBody from './components/CreateEditModalBody'
import { CIMSPage, Permission } from 'shared'
import {
  simpleCreateNotification,
  simpleEditNotification,
  simpleRemoveNotification,
  simpleRestoreNotification,
} from 'hooks/notificationHandler'
import { useUserRoleSelectContext } from 'components/CIMSSelect/common/UserRoleSelect/UserRoleSelectContext'
import { errorHandler } from 'hooks/errorHandler'
const Help = HelpMessage.FieldDesignChanges.Concurrences.Groups

const Groups: React.FC = () => {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const {
    data,
    loading: fetching,
    refetch,
  } = useProjectFdcConcurrenceGroupsQuery({
    ...DefaultQueryHookOptions,
    skip: !projectId,
    variables: {
      globalId: projectId!,
      input: { projectId: { equals: projectId } },
    },
  })
  const [create] = useCreateFdcConcurrenceGroupMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      createFDCConcurrenceGroup: {
        concurrenceGroup: { name },
      },
    }) => simpleCreateNotification(name),
  })
  const [edit] = useEditFdcConcurrenceGroupMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      editFDCConcurrenceGroup: {
        concurrenceGroup: { name },
      },
    }) => simpleEditNotification(name),
  })
  const [remove, { loading: removing }] =
    useSoftRemoveFdcConcurrenceGroupMutation({
      ...DefaultMutationHookOptions,
      onCompleted: ({
        softRemoveFDCConcurrenceGroup: {
          concurrenceGroup: { name },
        },
      }) => simpleRemoveNotification(name),
    })
  const [restore, { loading: restoring }] =
    useRestoreFdcConcurrenceGroupMutation({
      ...DefaultMutationHookOptions,
      onCompleted: ({
        restoreFDCConcurrenceGroup: {
          concurrenceGroup: { name },
        },
      }) => simpleRestoreNotification(name),
    })
  const columns: CIMSColumn<
    ProjectFdcConcurrenceGroupsQuery['project']['fdcConcurrenceGroups'][0]
  >[] = [
    useNameColumn({
      help: Help.Name,
      sort: { default: 'ascend' },
    }),
    useOptionalDescriptionColumn('Concurrence Group'),
    useUsersAndRolesColumn({
      title: 'Concurrers',
      help: Help.Concurrers,
    }),
  ]
  const { toLabeledValues, fromLabeledValues } = useUserRoleSelectContext()
  return (
    <>
      <CIMSTable
        records={data?.project.fdcConcurrenceGroups}
        rowKey="id"
        details={
          <Space size="small" direction="vertical">
            {`${Help.Concurrers}.`}
            All Concurrence Groups are associated with all Field Design Changes.
          </Space>
        }
        columns={columns}
        expand={{
          expandable: ({ fdcToFDCConcurrenceGroups }) =>
            fdcToFDCConcurrenceGroups.length > 0,
          render: ({ id, fdcToFDCConcurrenceGroups }) => ({
            additionalInfo: [
              {
                label: 'Associated FDCs',
                help: Help.AssociatedFDCs,
                type: 'deepLinks',
                showMore: { key: id },
                links: fdcToFDCConcurrenceGroups.map(
                  ({
                    fieldDesignChange: { id: fdcId, formattedNumber, deleted },
                  }) => ({
                    label: formattedNumber,
                    page: CIMSPage.FDCTracker,
                    deep: {
                      recordId: fdcId,
                      deleted,
                    },
                  })
                ),
              },
            ],
          }),
        }}
        loading={fetching || removing || restoring}
        actions={{
          header: {
            onReload: refetch,
            create: {
              permission: Permission.CreateFDCConcurrenceGroup,
              entity: 'Concurrence Group',
              loading: fetching,
              modal: {
                body: <CreateEditModalBody />,
                onOk: async ({ name, description, rolesUsers }) => {
                  const values = fromLabeledValues(rolesUsers)
                  if (!projectId)
                    return errorHandler('projectId is not defined')
                  await create({
                    variables: {
                      createFdcConcurrenceGroupInput: {
                        name,
                        projectId,
                        description,
                        ...values,
                      },
                    },
                  })
                  refetch()
                },
              },
            },
          },
          row: {
            edit: {
              permission: Permission.EditFDCConcurrenceGroup,
              modal: {
                body: <CreateEditModalBody />,
                initialValues: ({ name, roles, users, description }) => ({
                  name,
                  description,
                  rolesUsers: toLabeledValues({ users, roles }),
                }),
                recordName: 'name',
                onOk: async ({ id }, { name, description, rolesUsers }) => {
                  const values = fromLabeledValues(rolesUsers)
                  await edit({
                    variables: {
                      editFdcConcurrenceGroupInput: {
                        id,
                        description,
                        name,
                        ...values,
                      },
                    },
                  })
                  refetch()
                },
              },
            },
            remove: {
              permission: Permission.RemoveRestoreFDCConcurrenceGroup,
              disabled: ({ fdcToFDCConcurrenceGroups }) =>
                fdcToFDCConcurrenceGroups.length > 0
                  ? {
                      message:
                        'Cannot remove Concurrence Groups that have concurred on one or more Field Design Changes',
                    }
                  : false,
              onRemove: async ({ id }) => {
                await remove({ variables: { id } })
                refetch()
              },
            },
            restore: {
              permission: Permission.RemoveRestoreFDCConcurrenceGroup,
              onRestore: async ({ id }) => {
                await restore({ variables: { id } })
                refetch()
              },
              metadata: ({ deleted, deletedDate, deletedUser }) => ({
                removed: deleted,
                removedBy: deletedUser,
                removedDate: deletedDate,
              }),
              default: 'active',
            },
          },
        }}
      />
    </>
  )
}

export default Groups
